
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { X, User, Phone, Mail, MapPin, Tag, Edit2, Save, Trash2, Loader2, Navigation, Copy, ExternalLink, Globe, AlertTriangle, RotateCcw, Briefcase, Calendar, Fingerprint, Award } from 'lucide-react';
import { Contact, User as UserType, Task, Attendance } from '../types';
import { supabase } from '../supabase';
import { CONFIG } from '../config';
import { useFormDraft } from '../hooks/useFormDraft';

// Leaflet global declaration
declare var L: any;

interface ContactDetailModalProps {
  contact: Contact;
  categories: string[];
  currentUser: UserType;
  tasks?: Task[];
  attendances?: Attendance[];
  onOpenTask?: (taskId: string) => void;
  onOpenAttendance?: (attendanceId: string) => void;
  onClose: () => void;
  onUpdateContact: (id: string, updates: Partial<Contact>) => void;
  onDeleteContact: (id: string) => Promise<boolean>;
}

const ContactDetailModal: React.FC<ContactDetailModalProps> = ({
  contact,
  categories,
  currentUser,
  tasks: allTasks = [],
  attendances: allAttendances = [],
  onOpenTask,
  onOpenAttendance,
  onClose,
  onUpdateContact,
  onDeleteContact
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  
  const mapRef = useRef<any>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData, clearDraft, hasDraft] = useFormDraft(`edit:contact:${contact.id}`, {
    name: contact.name,
    type: contact.type,
    profession: contact.profession || '',
    birthdate: contact.birthdate || '',
    phone: contact.phones[0] || '',
    email: contact.emails[0] || '',
    street: contact.street || '',
    number: contact.number || '',
    neighborhood: contact.neighborhood || '',
    city: contact.city || '',
    zipcode: contact.zipcode || '',
    tags: contact.tags.join(', '),
    observations: contact.observations || '',
    support_level: contact.support_level || '',
    latitude: contact.latitude,
    longitude: contact.longitude,
    contact_origin: contact.contact_origin || '',
    voter_registration_number: contact.voter_registration_number || '',
    voter_zone: contact.voter_zone || '',
    voter_section: contact.voter_section || ''
  });

  const canEdit = currentUser.permissions.canEditCRM;

  // Initialize Leaflet Map
  useEffect(() => {
    if (!isEditing && contact.latitude && contact.longitude && mapContainerRef.current) {
      // Delay pequeno para garantir que o modal terminou a animação de entrada
      const timer = setTimeout(() => {
        if (!mapContainerRef.current) return;
        
        // Destruir mapa existente se houver
        if (mapRef.current) {
          mapRef.current.remove();
        }

        const latlng = [contact.latitude, contact.longitude];
        mapRef.current = L.map(mapContainerRef.current, {
          zoomControl: false,
          attributionControl: false
        }).setView(latlng, 15);

        L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
          maxZoom: 19
        }).addTo(mapRef.current);

        const icon = L.divIcon({
          className: 'custom-div-icon',
          html: `<div class="w-8 h-8 rounded-full bg-parliament-600 border-4 border-white shadow-xl flex items-center justify-center text-white"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg></div>`,
          iconSize: [32, 32],
          iconAnchor: [16, 32]
        });

        L.marker(latlng, { icon }).addTo(mapRef.current);
      }, 300);

      return () => {
        clearTimeout(timer);
        if (mapRef.current) {
          mapRef.current.remove();
          mapRef.current = null;
        }
      };
    }
  }, [isEditing, contact.latitude, contact.longitude]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await onUpdateContact(contact.id, {
        name: formData.name,
        type: formData.type,
        profession: formData.profession,
        birthdate: formData.birthdate || null,
        phones: formData.phone ? [formData.phone] : [],
        emails: formData.email ? [formData.email] : [],
        street: formData.street,
        number: formData.number,
        neighborhood: formData.neighborhood,
        city: formData.city,
        zipcode: formData.zipcode,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        observations: formData.observations,
        support_level: formData.support_level,
        latitude: formData.latitude,
        longitude: formData.longitude,
        contact_origin: formData.contact_origin,
        voter_registration_number: formData.voter_registration_number,
        voter_zone: formData.voter_zone,
        voter_section: formData.voter_section
      });
      clearDraft();
      setIsEditing(false);
    } catch (err: any) {
      console.error(err);
      setErrorMsg("Erro ao salvar contato.");
    } finally {
      setLoading(false);
    }
  };

  const handleGeocode = async () => {
    if (!formData.street || !formData.city) return;
    setGeoLoading(true);
    try {
      const requestId = crypto.randomUUID();
      const response = await fetch(`${CONFIG.supabaseUrl}/functions/v1/geocode-address`, {
        method: 'POST',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          'apikey': CONFIG.supabaseAnonKey,
          'Authorization': `Bearer ${CONFIG.supabaseAnonKey}`,
          'x-request-id': requestId
        },
        body: JSON.stringify({
          street: formData.street,
          number: formData.number,
          neighborhood: formData.neighborhood,
          city: formData.city,
          zipcode: formData.zipcode
        })
      });

      if (!response.ok) {
        alert("Erro ao conectar com serviço de mapas. Tente novamente.");
        return;
      }

      const data = await response.json();
      if (data && data.ok && data.latitude && data.longitude) {
        setFormData(prev => ({
          ...prev,
          latitude: data.latitude,
          longitude: data.longitude
        }));
        onUpdateContact(contact.id, { latitude: data.latitude, longitude: data.longitude });
      } else {
        alert("Endereço não localizado: " + (data?.error || "Verifique os dados informados."));
      }
    } catch (err) {
      alert("Erro ao processar localização.");
    } finally {
      setGeoLoading(false);
    }
  };

  const handleDelete = async () => {
    const success = await onDeleteContact(contact.id);
    if (success) onClose();
  };

  const hasVoterInfo = contact.voter_registration_number || contact.voter_zone || contact.voter_section;

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-navy-950/80 backdrop-blur-sm animate-in fade-in" onClick={onClose}>
      <div className="bg-white dark:bg-navy-900 w-full max-w-4xl rounded-[32px] shadow-2xl flex flex-col overflow-hidden border border-neutral-200 dark:border-navy-800 relative max-h-[95vh]" onClick={e => e.stopPropagation()}>
        
        {showDeleteConfirm && (
          <div className="absolute inset-0 z-[160] bg-navy-950/90 backdrop-blur-md flex flex-col items-center justify-center p-12 text-center animate-in fade-in">
             <Trash2 size={64} className="text-red-500 mb-6" />
             <h2 className="text-3xl font-black text-white mb-4 uppercase">Excluir Contato?</h2>
             <div className="flex gap-4">
                <button onClick={() => setShowDeleteConfirm(false)} className="px-10 py-4 bg-white/10 text-white rounded-2xl font-black uppercase text-xs">Cancelar</button>
                <button onClick={handleDelete} className="px-10 py-4 bg-red-600 text-white rounded-2xl font-black uppercase text-xs">Confirmar</button>
             </div>
          </div>
        )}

        <div className="px-10 py-8 border-b border-slate-100 dark:border-navy-800 flex items-center justify-between shrink-0 bg-white dark:bg-navy-900">
          <div className="flex items-center gap-4">
            <div className="relative w-16 h-16 rounded-[24px] overflow-hidden shadow-xl">
              <div className="w-16 h-16 bg-navy-950 dark:bg-white text-white dark:text-navy-950 flex items-center justify-center text-2xl font-black rounded-[24px]">
                {contact.name.charAt(0)}
              </div>
              {contact.photo_url && <img src={contact.photo_url} alt="" className="absolute inset-0 w-16 h-16 rounded-[24px] object-cover" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />}
            </div>
            <div>
              <h2 className="text-2xl font-black text-slate-800 dark:text-white tracking-tight">{isEditing ? 'Editar Perfil' : contact.name}</h2>
              {!isEditing && <p className="text-slate-400 text-xs font-bold uppercase tracking-widest">{contact.type} • {contact.city}</p>}
            </div>
          </div>
          <div className="flex items-center gap-3">
             {!isEditing && canEdit && (
               <>
                 <button onClick={() => setIsEditing(true)} className="flex items-center gap-2 px-6 py-2.5 bg-neutral-100 dark:bg-navy-800 text-neutral-600 rounded-xl font-black text-[10px] uppercase hover:bg-neutral-200 transition-colors">Editar</button>
                 <button onClick={() => setShowDeleteConfirm(true)} className="p-3 text-slate-300 hover:text-red-500 transition-all"><Trash2 size={20}/></button>
               </>
             )}
             {isEditing && hasDraft && (
               <button 
                type="button" 
                onClick={clearDraft}
                className="flex items-center gap-1.5 text-[9px] font-black text-red-500 uppercase tracking-widest hover:bg-red-50 px-3 py-2 rounded-xl transition-colors"
                title="Limpar rascunho de edição"
              >
                <RotateCcw size={14}/> Limpar Rascunho
              </button>
             )}
             <button onClick={onClose} className="p-3 hover:bg-neutral-100 rounded-full text-neutral-400"><X size={24} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar p-10 space-y-10">
          {isEditing ? (
            <div className="space-y-8 animate-in slide-in-from-left-4">
               {errorMsg && <div className="p-4 bg-red-50 text-red-600 rounded-2xl text-xs font-bold">{errorMsg}</div>}
               
               <div className="space-y-4">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest border-l-4 border-parliament-600 pl-3">Dados Pessoais</p>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Nome Completo</label>
                    <input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-xl px-4 py-3 text-sm font-bold dark:text-white" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Categoria</label>
                      <select value={formData.type} onChange={e => setFormData({...formData, type: e.target.value})} className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-xl px-4 py-3 text-sm font-bold dark:text-white">
                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Nível de Apoio</label>
                      <select value={formData.support_level} onChange={e => setFormData({...formData, support_level: e.target.value})} className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-xl px-4 py-3 text-sm font-bold dark:text-white">
                        <option value="">Indefinido</option>
                        <option value="Apoiador">Apoiador</option>
                        <option value="Neutro">Neutro</option>
                        <option value="Oposição">Oposição</option>
                        <option value="Liderança">Liderança</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Telefone</label>
                      <input value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-xl px-4 py-3 text-sm font-bold dark:text-white" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">E-mail</label>
                      <input value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-xl px-4 py-3 text-sm font-bold dark:text-white" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Profissão</label>
                      <input value={formData.profession} onChange={e => setFormData({...formData, profession: e.target.value})} className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-xl px-4 py-3 text-sm font-bold dark:text-white" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Data de Nascimento</label>
                      <input type="date" value={formData.birthdate} onChange={e => setFormData({...formData, birthdate: e.target.value})} className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-xl px-4 py-3 text-sm font-bold dark:text-white" />
                    </div>
                  </div>
               </div>

               <div className="space-y-4">
                  <div className="flex justify-between items-center">
                     <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest border-l-4 border-emerald-600 pl-3">Endereço & Localização</p>
                     <button type="button" onClick={handleGeocode} disabled={geoLoading} className="text-[9px] font-black uppercase text-emerald-600 hover:underline flex items-center gap-1">
                        {geoLoading ? <Loader2 size={10} className="animate-spin"/> : <Navigation size={10}/>} Atualizar Coordenadas
                     </button>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Logradouro</label>
                    <div className="flex gap-2">
                       <input value={formData.street} onChange={e => setFormData({...formData, street: e.target.value})} className="flex-[3] bg-slate-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-xl px-4 py-3 text-sm font-bold dark:text-white" />
                       <input value={formData.number} onChange={e => setFormData({...formData, number: e.target.value})} placeholder="Nº" className="flex-1 bg-slate-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-xl px-4 py-3 text-sm font-bold dark:text-white" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Bairro</label>
                      <input value={formData.neighborhood} onChange={e => setFormData({...formData, neighborhood: e.target.value})} className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-xl px-4 py-3 text-sm font-bold dark:text-white" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Cidade</label>
                      <input value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})} className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-xl px-4 py-3 text-sm font-bold dark:text-white" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">CEP</label>
                      <input value={formData.zipcode} onChange={e => setFormData({...formData, zipcode: e.target.value})} className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-xl px-4 py-3 text-sm font-bold dark:text-white" />
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Lat / Long</label>
                       <div className="flex gap-2">
                          <input readOnly value={formData.latitude || ''} placeholder="Lat" className="w-full bg-slate-100 dark:bg-navy-800 border-transparent rounded-xl px-2 py-3 text-xs font-mono dark:text-slate-400" />
                          <input readOnly value={formData.longitude || ''} placeholder="Long" className="w-full bg-slate-100 dark:bg-navy-800 border-transparent rounded-xl px-2 py-3 text-xs font-mono dark:text-slate-400" />
                       </div>
                    </div>
                  </div>
               </div>

               <div className="space-y-4">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest border-l-4 border-parliament-600 pl-3">Dados Eleitorais & Origem</p>
                  <div className="space-y-2">
                     <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Origem do Contato (Quem indicou?)</label>
                     <input value={formData.contact_origin} onChange={e => setFormData({...formData, contact_origin: e.target.value})} placeholder="Ex: Indicação de Fulano" className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-xl px-4 py-3 text-sm font-bold dark:text-white" />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                     <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Título de Eleitor</label>
                        <input value={formData.voter_registration_number} onChange={e => setFormData({...formData, voter_registration_number: e.target.value})} className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-xl px-4 py-3 text-sm font-bold dark:text-white" />
                     </div>
                     <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Zona</label>
                        <input value={formData.voter_zone} onChange={e => setFormData({...formData, voter_zone: e.target.value})} className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-xl px-4 py-3 text-sm font-bold dark:text-white" />
                     </div>
                     <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Seção</label>
                        <input value={formData.voter_section} onChange={e => setFormData({...formData, voter_section: e.target.value})} className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-xl px-4 py-3 text-sm font-bold dark:text-white" />
                     </div>
                  </div>
               </div>

               <div className="space-y-4">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest border-l-4 border-amber-600 pl-3">Observações & Etiquetas</p>
                  <div className="space-y-2">
                     <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Tags (separadas por vírgula)</label>
                     <input value={formData.tags} onChange={e => setFormData({...formData, tags: e.target.value})} className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-xl px-4 py-3 text-sm font-bold dark:text-white" />
                  </div>
                  <div className="space-y-2">
                     <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Notas Gerais</label>
                     <textarea value={formData.observations} onChange={e => setFormData({...formData, observations: e.target.value})} rows={4} className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-xl px-4 py-3 text-sm font-bold dark:text-white" />
                  </div>
               </div>

               <div className="flex justify-end gap-3 pt-6 border-t border-slate-100">
                  <button onClick={() => setIsEditing(false)} className="px-8 py-3 font-bold text-slate-400">Cancelar</button>
                  <button onClick={handleSave} disabled={loading} className="bg-navy-950 dark:bg-parliament-600 text-white px-12 py-5 rounded-3xl font-black text-[11px] uppercase tracking-widest shadow-2xl flex items-center gap-3 disabled:opacity-50">
                    {loading ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} SALVAR ALTERAÇÕES
                  </button>
               </div>
            </div>
          ) : (
            <div className="space-y-12 animate-in fade-in">
               <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                     <div className="bg-neutral-50 dark:bg-navy-800 p-6 rounded-3xl space-y-4 shadow-sm border border-neutral-100 dark:border-navy-700">
                        <div className="flex items-center gap-3 text-slate-500 dark:text-slate-400">
                           <Phone size={18} className="text-emerald-500"/>
                           <span className="font-bold text-sm text-navy-900 dark:text-white">{contact.phones[0] || 'Sem telefone'}</span>
                        </div>
                        <div className="flex items-center gap-3 text-slate-500 dark:text-slate-400">
                           <Mail size={18} className="text-parliament-500"/>
                           <span className="font-bold text-sm text-navy-900 dark:text-white">{contact.emails[0] || 'Sem e-mail'}</span>
                        </div>
                        {contact.birthdate && (
                          <div className="flex items-center gap-3 text-slate-500 dark:text-slate-400">
                             <Calendar size={18} />
                             <span className="font-bold text-sm text-navy-900 dark:text-white">
                               {contact.birthdate.split('-').reverse().join('/')}
                             </span>
                          </div>
                        )}
                        {contact.profession && (
                          <div className="flex items-center gap-3 text-slate-500 dark:text-slate-400">
                             <Briefcase size={18} />
                             <span className="font-bold text-sm text-navy-900 dark:text-white">{contact.profession}</span>
                          </div>
                        )}
                        <div className="flex items-start gap-3 text-slate-500 dark:text-slate-400">
                           <MapPin size={18} className="mt-0.5" />
                           <div>
                              <p className="font-bold text-sm text-navy-900 dark:text-white">
                                {contact.street ? `${contact.street}, ${contact.number}` : 'Endereço não cadastrado'}
                              </p>
                              {contact.neighborhood && <p className="text-xs">{contact.neighborhood} - {contact.city}</p>}
                           </div>
                        </div>
                        {contact.contact_origin && (
                           <div className="pt-3 border-t border-slate-100 dark:border-navy-700 flex items-center gap-3 text-slate-500 dark:text-slate-400">
                              <Award size={18} className="text-amber-500" />
                              <div>
                                 <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest leading-none mb-1">Origem / Indicação</p>
                                 <p className="font-bold text-sm text-navy-900 dark:text-white">{contact.contact_origin}</p>
                              </div>
                           </div>
                        )}
                     </div>
                     
                     <div className="flex flex-wrap gap-2">
                        {(contact.tags || []).map(tag => (
                           <span key={tag} className="px-3 py-1.5 bg-slate-100 dark:bg-navy-800 text-slate-600 dark:text-slate-300 rounded-lg text-xs font-bold border border-slate-200 dark:border-navy-700 flex items-center gap-1">
                              <Tag size={12}/> {tag}
                           </span>
                        ))}
                     </div>
                  </div>

                  <div className="space-y-6">
                     {hasVoterInfo && (
                        <div className="bg-parliament-50 dark:bg-parliament-900/10 p-6 rounded-3xl border border-parliament-100 dark:border-parliament-800/30">
                           <p className="text-[10px] font-black text-parliament-600 uppercase tracking-widest mb-4 flex items-center gap-2"><Fingerprint size={14}/> Dados Eleitorais</p>
                           <div className="grid grid-cols-2 gap-4">
                              <div>
                                 <p className="text-[9px] font-bold text-slate-400 uppercase">Título</p>
                                 <p className="text-sm font-black text-navy-950 dark:text-white">{contact.voter_registration_number || '-'}</p>
                              </div>
                              <div className="flex gap-6">
                                 <div>
                                    <p className="text-[9px] font-bold text-slate-400 uppercase">Zona</p>
                                    <p className="text-sm font-black text-navy-950 dark:text-white">{contact.voter_zone || '-'}</p>
                                 </div>
                                 <div>
                                    <p className="text-[9px] font-bold text-slate-400 uppercase">Seção</p>
                                    <p className="text-sm font-black text-navy-950 dark:text-white">{contact.voter_section || '-'}</p>
                                 </div>
                              </div>
                           </div>
                        </div>
                     )}

                     {contact.latitude && contact.longitude && (
                        <div className="h-48 rounded-3xl overflow-hidden relative group shadow-sm border border-neutral-100 dark:border-navy-800">
                           <div ref={mapContainerRef} className="w-full h-full z-10" />
                           <a 
                             href={`https://www.google.com/maps/search/?api=1&query=${contact.latitude},${contact.longitude}`} 
                             target="_blank" 
                             rel="noreferrer"
                             className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white font-bold gap-2 z-20"
                           >
                              <ExternalLink size={20}/> Abrir no Google Maps
                           </a>
                        </div>
                     )}
                     
                     {contact.observations && (
                        <div className="bg-amber-50 dark:bg-amber-900/10 p-6 rounded-3xl border border-amber-100 dark:border-amber-800/30">
                           <p className="text-[10px] font-black text-amber-600 uppercase tracking-widest mb-2 flex items-center gap-2"><AlertTriangle size={12}/> Observações</p>
                           <p className="text-sm text-slate-700 dark:text-slate-300 font-medium leading-relaxed whitespace-pre-line">{contact.observations}</p>
                        </div>
                     )}
                  </div>
               </div>

               {/* Related items */}
               {(() => {
                 const relTasks = allTasks.filter(t => t.contactid === contact.id);
                 const relAtts = allAttendances.filter(a => a.contactid === contact.id);
                 if (relTasks.length === 0 && relAtts.length === 0) return null;
                 return (
                   <div className="mt-8 space-y-6">
                     {relTasks.length > 0 && (
                       <div>
                         <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 border-l-4 border-blue-500 pl-3">Tarefas Relacionadas ({relTasks.length})</p>
                         <div className="space-y-2">
                           {relTasks.map(t => (
                             <div key={t.id} onClick={() => onOpenTask?.(t.id)} className={`flex items-start gap-3 p-4 bg-neutral-50 dark:bg-navy-950 rounded-2xl border border-neutral-100 dark:border-navy-800 ${onOpenTask ? 'cursor-pointer hover:border-parliament-300 hover:bg-parliament-50/50 dark:hover:bg-navy-800 transition-all' : ''}`}>
                               <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1.5 ${t.status === 'Concluído' ? 'bg-emerald-500' : t.status === 'Em andamento' ? 'bg-blue-500' : t.status === 'Aguardando' ? 'bg-amber-500' : 'bg-slate-400'}`} />
                               <div className="flex-1 min-w-0">
                                 <p className="text-sm font-bold text-navy-950 dark:text-white">{t.title}</p>
                                 <p className="text-[10px] text-slate-400 mt-0.5">{t.status}{t.duedate ? ` • ${new Date(t.duedate + 'T12:00:00').toLocaleDateString('pt-BR')}` : ''}</p>
                               </div>
                             </div>
                           ))}
                         </div>
                       </div>
                     )}
                     {relAtts.length > 0 && (
                       <div>
                         <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 border-l-4 border-emerald-500 pl-3">Atendimentos Relacionados ({relAtts.length})</p>
                         <div className="space-y-2">
                           {relAtts.map(a => (
                             <div key={a.id} onClick={() => onOpenAttendance?.(a.id)} className={`flex items-start gap-3 p-4 bg-neutral-50 dark:bg-navy-950 rounded-2xl border border-neutral-100 dark:border-navy-800 ${onOpenAttendance ? 'cursor-pointer hover:border-emerald-300 hover:bg-emerald-50/50 dark:hover:bg-navy-800 transition-all' : ''}`}>
                               <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1.5 ${a.status === 'Concluído' ? 'bg-emerald-500' : a.status === 'Em andamento' ? 'bg-blue-500' : 'bg-amber-500'}`} />
                               <div className="flex-1 min-w-0">
                                 <p className="text-sm font-bold text-navy-950 dark:text-white">{a.demand}</p>
                                 <p className="text-[10px] text-slate-400 mt-0.5">{a.status}{a.created_at ? ` • ${new Date(a.created_at).toLocaleDateString('pt-BR')}` : ''}</p>
                               </div>
                             </div>
                           ))}
                         </div>
                       </div>
                     )}
                   </div>
                 );
               })()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactDetailModal;
